import React, { useEffect, useMemo, useState } from "react";
//-tech-debt-react-query;
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, CircularProgress, Grid, Card, CardHeader,
  List, ListItemButton, ListItemText, ListItemIcon, Checkbox, Box
} from "@mui/material";
import { fetchItems, fetchItem, postItem, putItem } from "../../support/queries";
import Typography from "@mui/material/Typography";
import { HbrDivider } from "@harbor/elements-react-standalone";
import { CacheKey } from "../../types/cache-keys";
import AdminConfirmDialog from "../Admin/Common/AdminConfirmDialog";

type Role = { id: string | number; name: string };
type User = { id: string | number; name: string; email?: string };

export type UserGroup = {
  id?: string | number;
  group_id: string;
  name: string;
  description?: string;
  roles?: (string | number)[];
  users?: (string | number)[];
  __clone__?: boolean;
};

interface Props {
  open: boolean;
  editItem: UserGroup | null;
  onClose: () => void;
}

function not(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => !b.includes(value));
}
function intersection(a: readonly string[], b: readonly string[]): string[] {
  return a.filter((value) => b.includes(value));
}
function union(a: readonly string[], b: readonly string[]): string[] {
  return [...a, ...not(b, a)];
}

const TRANSFER_LIST_WIDTH = 320;

const UserGroupEditDialog: React.FC<Props> = ({ open, editItem, onClose }) => {
  const queryClient = useQueryClient();
  const userGroup = editItem;
  const isClone = !!userGroup?.__clone__;

  const [groupId, setGroupId] = useState(userGroup?.group_id ?? "");
  const [name, setName] = useState(userGroup?.name ?? "");
  const [description, setDescription] = useState(userGroup?.description ?? "");

  const [initialGroupId, setInitialGroupId] = useState("");
  const [initialName, setInitialName] = useState("");
  const [initialDescription, setInitialDescription] = useState("");

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<string[]>([]);
  const [initialAssignedRoles, setInitialAssignedRoles] = useState<string[]>([]);
  const [checkedRoles, setCheckedRoles] = useState<string[]>([]);

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<string[]>([]);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<string[]>([]);
  const [checkedUsers, setCheckedUsers] = useState<string[]>([]);

  const [leftUserSearch, setLeftUserSearch] = useState("");
  const [rightUserSearch, setRightUserSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const clonedName = userGroup?.name ? `${userGroup.name} - Copy` : "";

    setGroupId(isClone ? "" : userGroup?.group_id ?? "");
    setName(isClone ? clonedName : userGroup?.name ?? "");
    setDescription(userGroup?.description ?? "");

    setInitialGroupId(isClone ? "" : userGroup?.group_id ?? "");
    setInitialName(isClone ? clonedName : userGroup?.name ?? "");
    setInitialDescription(userGroup?.description ?? "");

    setLeftUserSearch("");
    setRightUserSearch("");

    setLoading(true);
    Promise.all([
      fetchItems("db/role"),
      fetchItems("db/user"),
      userGroup?.id ? fetchItem(`db/user-group/${userGroup.id}`) : Promise.resolve({ roles: [], users: [] }),
    ])
      .then(([roles, users, groupDetail]) => {
        setAllRoles(roles);
        setAllUsers(users);

        const rolesAssigned = ((groupDetail.roles || []) as (string | number)[]).map(String);
        const usersAssigned = ((groupDetail.users || []) as (string | number)[]).map(String);

        setAssignedRoles(rolesAssigned);
        setInitialAssignedRoles(rolesAssigned);

        setAssignedUsers(usersAssigned);
        setInitialAssignedUsers(usersAssigned);

        setCheckedRoles([]);
        setCheckedUsers([]);
      })
      .finally(() => setLoading(false));
  }, [open, userGroup, isClone]);

  const hasUnsavedChanges = useMemo(() => {
    const currentRoles = [...assignedRoles].sort();
    const initialRoles = [...initialAssignedRoles].sort();
    const currentUsers = [...assignedUsers].sort();
    const initialUsers = [...initialAssignedUsers].sort();

    return (
      groupId !== initialGroupId ||
      name !== initialName ||
      description !== initialDescription ||
      JSON.stringify(currentRoles) !== JSON.stringify(initialRoles) ||
      JSON.stringify(currentUsers) !== JSON.stringify(initialUsers)
    );
  }, [
    groupId,
    name,
    description,
    initialGroupId,
    initialName,
    initialDescription,
    assignedRoles,
    initialAssignedRoles,
    assignedUsers,
    initialAssignedUsers,
  ]);

  const handleRequestClose = () => {
    if (!hasUnsavedChanges || loading || saving) {
      onClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const allRoleIds = allRoles.map((r) => String(r.id));
  const leftRoles = not(allRoleIds, assignedRoles);
  const rightRoles = assignedRoles;
  const leftCheckedRoles = intersection(checkedRoles, leftRoles);
  const rightCheckedRoles = intersection(checkedRoles, rightRoles);

  const handleToggleRole = (value: string) => () => {
    setCheckedRoles((prev) => {
      const currentIndex = prev.indexOf(value);
      const newChecked = [...prev];
      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);
      return newChecked;
    });
  };

  const numberOfCheckedRoles = (items: readonly string[]) =>
    intersection(checkedRoles, items).length;

  const handleToggleAllRoles = (items: readonly string[]) => () => {
    if (numberOfCheckedRoles(items) === items.length) {
      setCheckedRoles(not(checkedRoles, items));
    } else {
      setCheckedRoles(union(checkedRoles, items));
    }
  };

  const handleCheckedRightRoles = () => {
    setAssignedRoles(union(assignedRoles, leftCheckedRoles));
    setCheckedRoles(not(checkedRoles, leftCheckedRoles));
  };

  const handleCheckedLeftRoles = () => {
    setAssignedRoles(not(assignedRoles, rightCheckedRoles));
    setCheckedRoles(not(checkedRoles, rightCheckedRoles));
  };

  const allUserIds = allUsers.map((u) => String(u.id));
  const leftUsers = not(allUserIds, assignedUsers);
  const rightUsers = assignedUsers;

  const matchesUserSearch = (user: User, term: string) => {
    const q = term.trim().toLowerCase();
    if (!q) return true;
    return (
      user.name?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q)
    );
  };

  const filteredLeftUsers = leftUsers.filter((id) => {
    const user = allUsers.find((u) => String(u.id) === id);
    return user ? matchesUserSearch(user, leftUserSearch) : false;
  });

  const filteredRightUsers = rightUsers.filter((id) => {
    const user = allUsers.find((u) => String(u.id) === id);
    return user ? matchesUserSearch(user, rightUserSearch) : false;
  });

  const leftCheckedUsers = intersection(checkedUsers, leftUsers);
  const rightCheckedUsers = intersection(checkedUsers, rightUsers);

  const handleToggleUser = (value: string) => () => {
    setCheckedUsers((prev) => {
      const currentIndex = prev.indexOf(value);
      const newChecked = [...prev];
      if (currentIndex === -1) newChecked.push(value);
      else newChecked.splice(currentIndex, 1);
      return newChecked;
    });
  };

  const numberOfCheckedUsers = (items: readonly string[]) =>
    intersection(checkedUsers, items).length;

  const handleToggleAllUsers = (items: readonly string[]) => () => {
    if (numberOfCheckedUsers(items) === items.length) {
      setCheckedUsers(not(checkedUsers, items));
    } else {
      setCheckedUsers(union(checkedUsers, items));
    }
  };

  const handleCheckedRightUsers = () => {
    setAssignedUsers(union(assignedUsers, leftCheckedUsers));
    setCheckedUsers(not(checkedUsers, leftCheckedUsers));
  };

  const handleCheckedLeftUsers = () => {
    setAssignedUsers(not(assignedUsers, rightCheckedUsers));
    setCheckedUsers(not(checkedUsers, rightCheckedUsers));
  };

  const customRoleList = (title: React.ReactNode, itemIds: readonly string[]) => (
    <Card sx={{ minWidth: TRANSFER_LIST_WIDTH, width: TRANSFER_LIST_WIDTH }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAllRoles(itemIds)}
            checked={numberOfCheckedRoles(itemIds) === itemIds.length && itemIds.length !== 0}
            indeterminate={
              numberOfCheckedRoles(itemIds) !== itemIds.length && numberOfCheckedRoles(itemIds) !== 0
            }
            disabled={itemIds.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={title}
        subheader={`${numberOfCheckedRoles(itemIds)}/${itemIds.length} selected`}
      />
      <HbrDivider />
      <List
        sx={{
          width: TRANSFER_LIST_WIDTH,
          height: 220,
          bgcolor: "background.paper",
          overflow: "auto",
        }}
        dense
        component="div"
        role="list"
      >
        {itemIds.map((id) => {
          const role = allRoles.find((r) => String(r.id) === id);
          if (!role) return null;
          const labelId = `transfer-list-role-item-${id}-label`;

          return (
            <ListItemButton
              key={id}
              role="listitem"
              onClick={handleToggleRole(id)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checkedRoles.includes(id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={role.name} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  const customUserList = (title: React.ReactNode, itemIds: readonly string[]) => (
    <Card sx={{ minWidth: TRANSFER_LIST_WIDTH, width: TRANSFER_LIST_WIDTH }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={
          <Checkbox
            onClick={handleToggleAllUsers(itemIds)}
            checked={numberOfCheckedUsers(itemIds) === itemIds.length && itemIds.length !== 0}
            indeterminate={
              numberOfCheckedUsers(itemIds) !== itemIds.length && numberOfCheckedUsers(itemIds) !== 0
            }
            disabled={itemIds.length === 0}
            inputProps={{
              "aria-label": "all items selected",
            }}
          />
        }
        title={title}
        subheader={`${numberOfCheckedUsers(itemIds)}/${itemIds.length} selected`}
      />
      <HbrDivider />
      <List
        sx={{
          width: TRANSFER_LIST_WIDTH,
          height: 220,
          bgcolor: "background.paper",
          overflow: "auto",
        }}
        dense
        component="div"
        role="list"
      >
        {itemIds.map((id) => {
          const user = allUsers.find((u) => String(u.id) === id);
          if (!user) return null;
          const labelId = `transfer-list-user-item-${id}-label`;

          return (
            <ListItemButton
              key={id}
              role="listitem"
              onClick={handleToggleUser(id)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checkedUsers.includes(id)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    "aria-labelledby": labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={user.name}
                secondary={user.email}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      let groupIdValue = isClone ? undefined : userGroup?.id;
      let responseGroup: any = null;

      // A group's join rows can point at users/roles that no longer exist (e.g. after a user-table
      // reload leaves orphaned `UserInGroup` rows). The backend echoes those phantom ids back on GET
      // but validates them on write, so a single stale id 400s the whole PUT ("Invalid pk ... does
      // not exist"). Drop any assigned id that isn't a currently-loaded user/role before saving: the
      // write succeeds and the serializer prunes the orphan (it deletes join rows not in the payload).
      // Guarded on a non-empty source list so a failed lookup can never blank out real memberships.
      const knownUserIds = new Set(allUsers.map((u) => String(u.id)));
      const knownRoleIds = new Set(allRoles.map((r) => String(r.id)));
      const usersToSave = allUsers.length
        ? assignedUsers.filter((id) => knownUserIds.has(id))
        : assignedUsers;
      const rolesToSave = allRoles.length
        ? assignedRoles.filter((id) => knownRoleIds.has(id))
        : assignedRoles;

      if (!groupIdValue) {
        responseGroup = await postItem("db/user-group", {
          name,
          description,
          roles: rolesToSave,
          users: usersToSave,
        });
        groupIdValue = responseGroup.id;
      } else {
        responseGroup = await putItem("db/user-group", {
          id: groupIdValue,
          group_id: groupId,
          name,
          description,
          roles: rolesToSave,
          users: usersToSave,
        });
      }

      if (responseGroup) {
        queryClient.invalidateQueries({ queryKey: [CacheKey.Admin, `userGroups`] });
      }

      setSaving(false);
      onClose();
    } catch (err) {
      setSaving(false);
      alert("Failed to save user group or assignments.");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleRequestClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isClone ? "Clone User Group" : userGroup?.id ? "Edit User Group" : "Add User Group"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            margin="normal"
          />

          <Typography sx={{ mt: 2, mb: 1, fontWeight: 500 }}>Assign Roles</Typography>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid item>{customRoleList("Available Roles", leftRoles)}</Grid>
            <Grid item>
              <Grid container direction="column" sx={{ alignItems: "center" }}>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRightRoles}
                  disabled={leftCheckedRoles.length === 0}
                  aria-label="move selected right"
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeftRoles}
                  disabled={rightCheckedRoles.length === 0}
                  aria-label="move selected left"
                >
                  &lt;
                </Button>
              </Grid>
            </Grid>
            <Grid item>{customRoleList("Assigned Roles", rightRoles)}</Grid>
          </Grid>

          <Typography sx={{ mt: 3, mb: 1, fontWeight: 500 }}>Assign Users</Typography>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid item>
              <Box sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search available users"
                  value={leftUserSearch}
                  onChange={(e) => setLeftUserSearch(e.target.value)}
                />
              </Box>
              {customUserList("Available Users", filteredLeftUsers)}
            </Grid>

            <Grid item>
              <Grid container direction="column" sx={{ alignItems: "center" }}>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRightUsers}
                  disabled={leftCheckedUsers.length === 0}
                  aria-label="move selected right"
                >
                  &gt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeftUsers}
                  disabled={rightCheckedUsers.length === 0}
                  aria-label="move selected left"
                >
                  &lt;
                </Button>
              </Grid>
            </Grid>

            <Grid item>
              <Box sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Search assigned users"
                  value={rightUserSearch}
                  onChange={(e) => setRightUserSearch(e.target.value)}
                />
              </Box>
              {customUserList("Assigned Users", filteredRightUsers)}
            </Grid>
          </Grid>

          {(loading || saving) && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRequestClose} disabled={loading || saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading || saving} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message="You have unsaved changes in this user group editor. If you continue, your changes will be lost."
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </>
  );
};

export default UserGroupEditDialog;